import { fetcher } from '@commenda/utils';
import {
  AM_TP_DEMO_ROUTES,
  TAmDemoMatrixExportQuery,
  TAmDemoResult,
} from './types';

const triggerBlobDownload = (blob: Blob, fileName: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Downloads the entity's local disclosure — Germany's Annex B Transaktionsmatrix, or Australia's IDS Section A.
 *
 * The file is regenerated from the confirmed rows on every call, so it always reflects the current state rather
 * than a stored copy — no cache to invalidate.
 *
 * Browsers do not expose `Content-Disposition` to a `fetch` + `Blob` download, so the name is rebuilt here from
 * the stem the scan payload carried. Taken from the caller rather than held as a constant: which requirement is
 * being exported follows the entity, and a hardcoded stem named every jurisdiction's export after Germany's.
 */
export async function exportAmTpDemoMatrix(
  query: TAmDemoMatrixExportQuery & { fileNameStem: string }
): Promise<TAmDemoResult<void>> {
  try {
    const response = await fetcher(
      `${AM_TP_DEMO_ROUTES.exportMatrix}?corporationId=${query.corporationId}&fiscalYear=${query.fiscalYear}`,
      { method: 'GET' }
    );

    if (!response.ok) {
      return {
        success: false,
        error: 'Failed to export the transaction matrix',
      };
    }

    triggerBlobDownload(
      await response.blob(),
      `${query.fileNameStem}_FY${query.fiscalYear}.xlsx`
    );

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to export the transaction matrix',
    };
  }
}
